import { useEffect, useRef, type ReactNode } from 'react'
import styles from './MergedComposer.module.css'
import { JourneyIcon, SparkleIcon } from './icons'
import { Pressable } from '../Pressable'

/**
 * MergedComposer — the locked shell for the AI Magic tab-into-composer
 * merge (sign-off design, see MergedComposer.module.css for the
 * invariants). Renders the tab-bar as a child of the composer so the
 * two read as one continuous outlined shape. The composer body
 * (textarea, attachment chips, toolbar) is passed as `children`.
 *
 * Do NOT wrap the tab-bar in extra containers or restyle the outer
 * class names — the geometry relies on the tab-bar being a direct
 * child of the composer element rendered here.
 */

const ICON_MAP = {
  sparkle: SparkleIcon,
  journey: JourneyIcon,
} as const

export type MergedComposerIcon = keyof typeof ICON_MAP | ReactNode

export type MergedComposerTab<K extends string = string> = {
  key: K
  label: string
  icon?: MergedComposerIcon
  ariaLabel?: string
}

interface MergedComposerProps<K extends string> {
  tabs: readonly MergedComposerTab<K>[]
  activeKey: K
  onTabChange: (key: K) => void
  ariaLabel: string
  hideTabBar?: boolean
  /** Render only the active tab, non-interactive. Used on existing
   * chats where the operating mode is fixed for the life of the
   * session — the user should see which lane they are in but cannot
   * switch it. Ignored when `hideTabBar` is true. */
  lockActiveTab?: boolean
  /** Optional extra class on the outer composer element for callers
   * that need to layer non-shell rules (spacing, width, etc.). */
  composerClassName?: string
  children: ReactNode
}

const renderIcon = (icon: MergedComposerIcon | undefined) => {
  if (!icon) return null
  if (typeof icon === 'string' && icon in ICON_MAP) {
    const Component = ICON_MAP[icon as keyof typeof ICON_MAP]
    return (
      <span className={styles.mcTabGlyph}>
        <Component />
      </span>
    )
  }
  return <span className={styles.mcTabGlyph}>{icon as ReactNode}</span>
}

export const MergedComposer = <K extends string>({
  tabs,
  activeKey,
  onTabChange,
  ariaLabel,
  hideTabBar = false,
  lockActiveTab = false,
  composerClassName,
  children,
}: MergedComposerProps<K>) => {
  const composerRef = useRef<HTMLDivElement | null>(null)

  // Dev-time invariant guard. Runs once on mount; logs a clear error
  // if the outer CSS cascade has clobbered one of the load-bearing
  // properties. Elevated to console.error so a regression trips the
  // dev-tools filter, not just a warning stream.
  useEffect(() => {
    if (import.meta.env.MODE === 'production') return
    const el = composerRef.current
    if (!el) return
    const cs = getComputedStyle(el)
    const problems: string[] = []
    const stroke = cs.borderTopWidth
    if (stroke !== '6px') {
      problems.push(`border-top-width is "${stroke}" (expected "6px")`)
    }
    const hasGradientBorder = cs.backgroundImage.includes('linear-gradient')
      && (cs.backgroundImage.includes('rgb')
        || cs.backgroundImage.includes('color(')
        || cs.backgroundImage.includes('color-mix')
        || cs.backgroundImage.includes('var('))
    if (!hasGradientBorder) {
      problems.push(`background-image lost its gradient stack ("${cs.backgroundImage}")`)
    }
    if (problems.length > 0) {
      // eslint-disable-next-line no-console
      console.error(
        '[MergedComposer] Locked shell styles were overridden by an outer stylesheet. '
        + 'Fix the offending rule instead of nudging values here.\n  · '
        + problems.join('\n  · '),
      )
    }
  }, [])

  return (
    <div className={styles.mcRoot}>
      <div
        ref={composerRef}
        className={`${styles.mcComposer}${composerClassName ? ` ${composerClassName}` : ''}`}
      >
        {!hideTabBar ? (
          <div className={styles.mcTabBar} role="tablist" aria-label={ariaLabel}>
            {tabs
              .filter((tab) => (lockActiveTab ? tab.key === activeKey : true))
              .map((tab) => {
              const isActive = tab.key === activeKey
              const className = `${styles.mcTab}${isActive ? ` ${styles.mcIsActive}` : ''}${lockActiveTab && isActive ? ` ${styles.mcIsLocked}` : ''}`
              return (
                <Pressable
                  key={tab.key}
                  role="tab"
                  aria-selected={isActive}
                  aria-disabled={lockActiveTab && isActive ? true : undefined}
                  tabIndex={lockActiveTab && isActive ? -1 : undefined}
                  aria-label={tab.ariaLabel ?? tab.label}
                  className={className}
                  onClick={() => {
                    if (lockActiveTab) return
                    if (!isActive) onTabChange(tab.key)
                  }}
                >
                  {renderIcon(tab.icon)}
                  <span>{tab.label}</span>
                </Pressable>
              )
            })}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  )
}
